import { Fragment, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useRetentionPolicyList } from '../hooks/useRetentionPolicyList';
import { useCreatePolicy } from '../hooks/useCreatePolicy';
import { useUpdatePolicy } from '../hooks/useUpdatePolicy';
import { useDeletePolicy } from '../hooks/useDeletePolicy';
import { useDryRun } from '../hooks/useDryRun';
import { usePurge } from '../hooks/usePurge';
import { RetentionPolicyFormDialog } from '../components/RetentionPolicyFormDialog';
import { RetentionPolicyDetailDrawer } from '../components/RetentionPolicyDetailDrawer';
import { DryRunResultPanel } from '../components/DryRunResultPanel';
import { PurgeConfirmDialog } from '../components/PurgeConfirmDialog';
import { RetentionCommonUsagePanel } from '../components/RetentionCommonUsagePanel';
import type { RetentionPolicyListItem, ApiError, RetentionRunStatus, DryRunResponse, RetentionPolicyResponse } from '../types/retentionAdmin';
import type { RetentionPolicyFormValues } from '../schema/retentionPolicy.schema';

const DEFAULT_PAGE_SIZE = 20;

function enabledTone(enabled: boolean): StatusTone {
  return enabled ? 'success' : 'neutral';
}

function runStatusTone(s: RetentionRunStatus | null): StatusTone {
  if (s === 'SUCCESS') return 'success';
  if (s === 'ABORTED' || s === 'FAILED') return 'danger';
  if (s === 'SKIPPED') return 'warning';
  return 'neutral';
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 13) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * RetentionPolicyListPage — 보존 정책 목록 (/retention-admin)
 * 5상태: loading / empty / error / default / dialog(form+purge+detail)
 */
export function RetentionPolicyListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 필터 상태
  const [tableFilter, setTableFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [keywordFilter, setKeywordFilter] = useState('');
  const [page, setPage] = useState(0);

  // 적용된 필터 (검색 버튼 클릭 시 반영)
  const [appliedFilters, setAppliedFilters] = useState({
    table: '',
    mode: '',
    enabled: '',
    keyword: '',
    page: 0,
  });

  // 다이얼로그 상태
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<RetentionPolicyResponse | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<RetentionPolicyListItem | null>(null);
  const [dryRunResults, setDryRunResults] = useState<Map<number, DryRunResponse>>(new Map());
  const [deleteTarget, setDeleteTarget] = useState<RetentionPolicyListItem | null>(null);

  // 토스트
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'warning' | 'error' } | null>(null);

  // 서버 오류 (폼 다이얼로그용)
  const [formServerError, setFormServerError] = useState<string | null>(null);

  const listQuery = useRetentionPolicyList({
    table: appliedFilters.table || undefined,
    mode: (appliedFilters.mode as RetentionPolicyListItem['mode']) || undefined,
    enabled: appliedFilters.enabled || undefined,
    keyword: appliedFilters.keyword || undefined,
    page: appliedFilters.page,
    size: DEFAULT_PAGE_SIZE,
    sort: 'name,asc',
  });

  const createMutation = useCreatePolicy();
  const updateMutation = useUpdatePolicy();
  const deleteMutation = useDeletePolicy();
  const dryRunMutation = useDryRun();
  const purgeMutation = usePurge();

  const pageData = listQuery.data;
  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  const activeCount = pageData?.content.filter((p) => p.enabled).length ?? 0;
  const inactiveCount = (pageData?.content.length ?? 0) - activeCount;

  const showToast = useCallback(
    (msg: string, type: 'success' | 'warning' | 'error' = 'success') => {
      setToast({ msg, type });
      setTimeout(() => setToast(null), 4000);
    },
    []
  );

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({
      table: tableFilter,
      mode: modeFilter,
      enabled: enabledFilter,
      keyword: keywordFilter,
      page: 0,
    });
  }, [tableFilter, modeFilter, enabledFilter, keywordFilter]);

  const handleReset = useCallback(() => {
    setTableFilter('');
    setModeFilter('');
    setEnabledFilter('');
    setKeywordFilter('');
    setPage(0);
    setAppliedFilters({ table: '', mode: '', enabled: '', keyword: '', page: 0 });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleFormSubmit = useCallback(
    (values: RetentionPolicyFormValues) => {
      setFormServerError(null);
      if (formMode === 'create') {
        createMutation.mutate(
          {
            name: values.name,
            description: values.description,
            targetTable: values.targetTable,
            mode: values.mode,
            timeColumn: values.timeColumn,
            retentionDays: values.retentionDays ?? undefined,
            graceDays: values.graceDays ?? undefined,
            batchSize: values.batchSize ?? undefined,
            enabled: values.enabled,
          },
          {
            onSuccess: () => {
              setFormMode(null);
              showToast(t('retentionAdmin.toast.created'));
            },
            onError: (err) => {
              const apiErr = err as unknown as ApiError;
              setFormServerError(
                t(`retentionAdmin.error.${apiErr.code}`, {
                  defaultValue: apiErr.message ?? t('retentionAdmin.error.network'),
                })
              );
            },
          }
        );
      } else if (formMode === 'edit' && editTarget) {
        updateMutation.mutate(
          {
            id: editTarget.id,
            body: {
              name: values.name,
              description: values.description,
              mode: values.mode,
              timeColumn: values.timeColumn,
              retentionDays: values.retentionDays ?? undefined,
              graceDays: values.graceDays ?? undefined,
              batchSize: values.batchSize ?? undefined,
              enabled: values.enabled,
            },
          },
          {
            onSuccess: () => {
              setFormMode(null);
              setEditTarget(null);
              showToast(t('retentionAdmin.toast.updated'));
            },
            onError: (err) => {
              const apiErr = err as unknown as ApiError;
              setFormServerError(
                t(`retentionAdmin.error.${apiErr.code}`, {
                  defaultValue: apiErr.message ?? t('retentionAdmin.error.network'),
                })
              );
            },
          }
        );
      }
    },
    [formMode, editTarget, createMutation, updateMutation, showToast, t]
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(t('retentionAdmin.toast.deleted'));
      },
      onError: (err) => {
        setDeleteTarget(null);
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`retentionAdmin.error.${apiErr.code}`, {
            defaultValue: t('retentionAdmin.error.network'),
          }),
          'error'
        );
      },
    });
  }, [deleteTarget, deleteMutation, showToast, t]);

  const handleDryRun = useCallback(
    (item: RetentionPolicyListItem) => {
      dryRunMutation.mutate(item.id, {
        onSuccess: (result) => {
          setDryRunResults((prev) => new Map(prev).set(item.id, result));
        },
        onError: (err) => {
          const apiErr = err as unknown as ApiError;
          showToast(
            t(`retentionAdmin.error.${apiErr.code}`, {
              defaultValue: t('retentionAdmin.error.network'),
            }),
            'error'
          );
        },
      });
    },
    [dryRunMutation, showToast, t]
  );

  const handlePurgeConfirm = useCallback(
    (confirmName: string, dryRunToken: string) => {
      if (!purgeTarget) return;
      purgeMutation.mutate(
        { id: purgeTarget.id, body: { confirmName, dryRunToken } },
        {
          onSuccess: (result) => {
            setPurgeTarget(null);
            setDryRunResults((prev) => {
              const next = new Map(prev);
              next.delete(purgeTarget.id);
              return next;
            });
            if (result.status === 'SUCCESS') {
              showToast(
                t('retentionAdmin.purge.success', { deleted: result.deleted.toLocaleString() })
              );
            } else if (result.status === 'ABORTED') {
              showToast(t('retentionAdmin.purge.aborted'), 'warning');
            } else if (result.status === 'SKIPPED') {
              showToast(t('retentionAdmin.purge.skipped'), 'warning');
            }
          },
          onError: (err) => {
            const apiErr = err as unknown as ApiError;
            if (apiErr.code === 'RETENT-409-002') {
              // dry-run 토큰 만료 — dry-run 재실행 유도
              showToast(t('retentionAdmin.error.RETENT-409-002'), 'error');
              setPurgeTarget(null);
            } else {
              showToast(
                t(`retentionAdmin.error.${apiErr.code}`, {
                  defaultValue: t('retentionAdmin.error.network'),
                }),
                'error'
              );
            }
          },
        }
      );
    },
    [purgeTarget, purgeMutation, showToast, t]
  );

  const TABLE_HEADERS = useMemo(
    () => [
      t('retentionAdmin.list.column.name'),
      t('retentionAdmin.list.column.table'),
      t('retentionAdmin.list.column.mode'),
      t('retentionAdmin.list.column.period'),
      t('retentionAdmin.list.column.status'),
      t('retentionAdmin.list.column.lastRun'),
      t('retentionAdmin.list.column.actions'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const selectStyle = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('retentionAdmin.list.title')}
        breadcrumb={<span>{t('retentionAdmin.list.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/retention-admin/history')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('retentionAdmin.list.button.history')}
            </button>
            <ButtonGuard requirePermission="RETENTION_MANAGE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={() => {
                    setFormServerError(null);
                    setEditTarget(null);
                    setFormMode('create');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  + {t('retentionAdmin.list.button.add')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('retentionAdmin.list.subtitle', {
            active: activeCount,
            inactive: inactiveCount,
            total: pageData.totalElements,
          })}
        </p>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('retentionAdmin.list.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 대상 테이블 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.list.filter.table')}
          <select
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('retentionAdmin.list.filter.tableAll')}</option>
            <option value="biz_auditlog_audit_log">biz_auditlog_audit_log</option>
            <option value="cmn_scheduler_run_history">cmn_scheduler_run_history</option>
            <option value="cmn_security_audit_log">cmn_security_audit_log</option>
            <option value="cmn_batch_job_execution">cmn_batch_job_execution</option>
            <option value="cmn_messaging_event_dlq">cmn_messaging_event_dlq</option>
            <option value="cmn_core_idempotency_key">cmn_core_idempotency_key</option>
            <option value="cmn_security_token_blacklist">cmn_security_token_blacklist</option>
            <option value="cmn_observability_slow_query">cmn_observability_slow_query</option>
          </select>
        </label>

        {/* 모드 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.list.filter.mode')}
          <select
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('retentionAdmin.list.filter.modeAll')}</option>
            <option value="ABSOLUTE">{t('retentionAdmin.mode.ABSOLUTE')}</option>
            <option value="SOFT_THEN_HARD">{t('retentionAdmin.mode.SOFT_THEN_HARD')}</option>
          </select>
        </label>

        {/* 상태 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.list.filter.enabled')}
          <select
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('retentionAdmin.list.filter.enabledAll')}</option>
            <option value="true">{t('retentionAdmin.list.filter.enabledTrue')}</option>
            <option value="false">{t('retentionAdmin.list.filter.enabledFalse')}</option>
          </select>
        </label>

        {/* 키워드 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.list.filter.keyword')}
          <input
            type="text"
            value={keywordFilter}
            onChange={(e) => setKeywordFilter(e.target.value)}
            placeholder={t('retentionAdmin.list.filter.keywordPlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 160,
            }}
          />
        </label>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('retentionAdmin.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('retentionAdmin.list.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('retentionAdmin.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('retentionAdmin.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('retentionAdmin.error.forbidden')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('retentionAdmin.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('retentionAdmin.list.empty.title')}
              description={t('retentionAdmin.list.empty.description')}
              action={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                  <ButtonGuard requirePermission="RETENTION_MANAGE">
                    {({ disabled, reason }) => (
                      <button
                        type="button"
                        disabled={disabled}
                        title={reason}
                        onClick={() => {
                          setFormServerError(null);
                          setEditTarget(null);
                          setFormMode('create');
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: 'var(--spacing-2) var(--spacing-4)',
                          background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                          color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          fontFamily: 'var(--font-family-sans)',
                        }}
                      >
                        + {t('retentionAdmin.list.button.add')}
                      </button>
                    )}
                  </ButtonGuard>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {t('retentionAdmin.list.empty.allowlist')}
                  </p>
                </div>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div
              role="region"
              aria-label={t('retentionAdmin.list.tableAriaLabel')}
              style={{ overflowX: 'auto' }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('retentionAdmin.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => {
                    const dryRunResult = dryRunResults.get(row.id) ?? null;
                    const isDryRunPending = dryRunMutation.isPending && dryRunMutation.variables === row.id;
                    const isPurgePending = purgeMutation.isPending && purgeTarget?.id === row.id;

                    // #634: bare fragment 에 key 부재 — keyed Fragment 로 정정
                    return (
                      <Fragment key={row.id}>
                        <tr
                          key={row.id}
                          aria-label={t('retentionAdmin.list.rowAriaLabel', { name: row.name })}
                          style={{ transition: 'background 0.1s', cursor: 'pointer' }}
                          onClick={() => setDetailId(row.id)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background =
                              'var(--color-surface-muted)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background = '';
                          }}
                        >
                          {/* 정책명 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              fontWeight: 600,
                              color: 'var(--color-text-primary)',
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.name}
                          </td>

                          {/* 대상 테이블 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)',
                              fontFamily: 'var(--font-family-mono)',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.targetTable}
                          </td>

                          {/* 모드 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {t(`retentionAdmin.mode.${row.mode}`, { defaultValue: row.mode })}
                          </td>

                          {/* 보존/유예 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.mode === 'ABSOLUTE' && row.retentionDays != null
                              ? t('retentionAdmin.list.period.retentionDays', { days: row.retentionDays })
                              : row.graceDays != null
                              ? t('retentionAdmin.list.period.graceDays', { days: row.graceDays })
                              : '-'}
                          </td>

                          {/* 상태 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <StatusPill tone={enabledTone(row.enabled)}>
                              {row.enabled
                                ? t('retentionAdmin.list.status.enabled')
                                : t('retentionAdmin.list.status.disabled')}
                            </StatusPill>
                          </td>

                          {/* 최근 실행 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              fontSize: 'var(--font-size-xs)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.lastRunStatus ? (
                              <StatusPill tone={runStatusTone(row.lastRunStatus)}>
                                {t(`retentionAdmin.status.${row.lastRunStatus}`, {
                                  defaultValue: row.lastRunStatus,
                                })}
                              </StatusPill>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                            )}
                          </td>

                          {/* 동작 버튼 */}
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              borderBottom: dryRunResult ? 'none' : '1px solid var(--color-surface-border)',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                display: 'flex',
                                gap: 'var(--spacing-1)',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                              }}
                            >
                              {/* 미리보기 */}
                              <button
                                type="button"
                                disabled={isDryRunPending}
                                onClick={() => handleDryRun(row)}
                                aria-label={`${row.name} ${t('retentionAdmin.list.action.dryRun')}`}
                                style={{
                                  padding: 'var(--spacing-1) var(--spacing-2)',
                                  background: 'transparent',
                                  border: '1px solid var(--color-surface-border)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  color: isDryRunPending ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                                  cursor: isDryRunPending ? 'not-allowed' : 'pointer',
                                  fontFamily: 'var(--font-family-sans)',
                                }}
                              >
                                {isDryRunPending ? '...' : t('retentionAdmin.list.action.dryRun')}
                              </button>

                              {/* purge */}
                              <ButtonGuard requirePermission="RETENTION_PURGE">
                                {({ disabled, reason }) => (
                                  <button
                                    type="button"
                                    disabled={disabled || isPurgePending}
                                    title={reason}
                                    onClick={() => setPurgeTarget(row)}
                                    aria-label={`${row.name} ${t('retentionAdmin.list.action.purge')}`}
                                    style={{
                                      padding: 'var(--spacing-1) var(--spacing-2)',
                                      background: 'transparent',
                                      border: `1px solid ${
                                        disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'
                                      }`,
                                      borderRadius: 'var(--radius-sm)',
                                      fontSize: 'var(--font-size-xs)',
                                      color: disabled
                                        ? 'var(--color-text-muted)'
                                        : 'var(--color-danger)',
                                      cursor: disabled ? 'not-allowed' : 'pointer',
                                      fontFamily: 'var(--font-family-sans)',
                                      opacity: disabled ? 0.5 : 1,
                                    }}
                                  >
                                    {isPurgePending ? '...' : t('retentionAdmin.list.action.purge')}
                                  </button>
                                )}
                              </ButtonGuard>

                              {/* 수정 */}
                              <ButtonGuard requirePermission="RETENTION_MANAGE">
                                {({ disabled, reason }) => (
                                  <button
                                    type="button"
                                    disabled={disabled}
                                    title={reason}
                                    onClick={() => {
                                      setFormServerError(null);
                                      // 전체 policy response 가 필요하므로 detail API 경유
                                      // 여기선 list item 으로 초기값 설정
                                      setEditTarget(row as unknown as RetentionPolicyResponse);
                                      setFormMode('edit');
                                    }}
                                    aria-label={`${row.name} ${t('retentionAdmin.list.action.edit')}`}
                                    style={{
                                      padding: 'var(--spacing-1) var(--spacing-2)',
                                      background: 'transparent',
                                      border: '1px solid var(--color-surface-border)',
                                      borderRadius: 'var(--radius-sm)',
                                      fontSize: 'var(--font-size-xs)',
                                      color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                                      cursor: disabled ? 'not-allowed' : 'pointer',
                                      fontFamily: 'var(--font-family-sans)',
                                      opacity: disabled ? 0.5 : 1,
                                    }}
                                  >
                                    {t('retentionAdmin.list.action.edit')}
                                  </button>
                                )}
                              </ButtonGuard>

                              {/* 삭제 */}
                              <ButtonGuard requirePermission="RETENTION_MANAGE">
                                {({ disabled, reason }) => (
                                  <button
                                    type="button"
                                    disabled={disabled}
                                    title={reason}
                                    onClick={() => setDeleteTarget(row)}
                                    aria-label={`${row.name} ${t('retentionAdmin.list.action.delete')}`}
                                    style={{
                                      padding: 'var(--spacing-1) var(--spacing-2)',
                                      background: 'transparent',
                                      border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                                      borderRadius: 'var(--radius-sm)',
                                      fontSize: 'var(--font-size-xs)',
                                      color: disabled ? 'var(--color-text-muted)' : 'var(--color-danger)',
                                      cursor: disabled ? 'not-allowed' : 'pointer',
                                      fontFamily: 'var(--font-family-sans)',
                                      opacity: disabled ? 0.5 : 1,
                                    }}
                                  >
                                    {t('retentionAdmin.list.action.delete')}
                                  </button>
                                )}
                              </ButtonGuard>
                            </div>
                          </td>
                        </tr>

                        {/* dry-run 결과 인라인 */}
                        {dryRunResult && (
                          <tr key={`${row.id}-dryrun`}>
                            <td
                              colSpan={TABLE_HEADERS.length}
                              style={{
                                padding: '0 var(--spacing-4) var(--spacing-3)',
                                borderBottom: '1px solid var(--color-surface-border)',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DryRunResultPanel result={dryRunResult} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('retentionAdmin.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav
                aria-label={t('retentionAdmin.list.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('retentionAdmin.list.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('retentionAdmin.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-border)'
                      }`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('retentionAdmin.list.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <RetentionCommonUsagePanel />

      {/* 정책 폼 다이얼로그 (dialog 상태) */}
      {formMode && (
        <RetentionPolicyFormDialog
          mode={formMode}
          initial={editTarget ?? undefined}
          isPending={createMutation.isPending || updateMutation.isPending}
          serverError={formServerError}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setFormMode(null);
            setEditTarget(null);
            setFormServerError(null);
          }}
        />
      )}

      {/* 정책 상세 드로어 (dialog 상태) */}
      {detailId !== null && (
        <RetentionPolicyDetailDrawer
          policyId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={() => {
            // 상세에서 수정으로 전환 — 드로어에서 폼 팝업
            setDetailId(null);
          }}
        />
      )}

      {/* purge 확인 다이얼로그 (dialog 상태) */}
      {purgeTarget && (
        <PurgeConfirmDialog
          policyId={purgeTarget.id}
          policyName={purgeTarget.name}
          targetTable={purgeTarget.targetTable}
          dryRunResult={dryRunResults.get(purgeTarget.id) ?? null}
          isPending={purgeMutation.isPending}
          onConfirm={handlePurgeConfirm}
          onCancel={() => setPurgeTarget(null)}
          onRunDryRun={() => {
            handleDryRun(purgeTarget);
          }}
        />
      )}

      {/* 삭제 confirm 다이얼로그 */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setDeleteTarget(null);
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="delete-dialog-title"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('retentionAdmin.confirm.delete.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('retentionAdmin.confirm.delete.body')}
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-mono)',
                marginBottom: 'var(--spacing-5)',
              }}
            >
              "{deleteTarget.name}"
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('retentionAdmin.confirm.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                autoFocus
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteMutation.isPending ? 0.6 : 1,
                }}
              >
                {deleteMutation.isPending ? '...' : t('retentionAdmin.confirm.delete.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background:
              toast.type === 'error'
                ? 'var(--color-danger)'
                : toast.type === 'warning'
                ? 'var(--color-warning)'
                : 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* 반응형 + 애니메이션 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 640px; }
        }
      `}</style>
    </div>
  );
}
